import { CheckCard } from '@ant-design/pro-components';
import { Badge, Empty, List, Typography, theme } from 'antd';
import { RibbonProps } from 'antd/es/badge/Ribbon';
import { FC, ReactElement } from 'react';
import { Alarm, Calendar2Check, Calendar4Week } from 'react-bootstrap-icons';

import { AwesomeNodePath } from '@/common/components';
import { BaseQueryParams } from '@/common/interfaces';

import { useGetPagesBulkPublishListQuery } from '@/modules/Pages';

import './PagesBulkPublishPagesList.scss';

interface Props {
  currentPage: number;
  pagesListItems: BaseQueryParams<typeof useGetPagesBulkPublishListQuery>['data']['items'];
  pagesListTotalItems: number;
  pagesListItemsPerPage: number;
  isLoading: boolean;
  disabled: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  handleChangePage: (page: number) => void;
}

interface ListItemProps extends Pick<Props['pagesListItems'][number], 'id' | 'title' | 'publishedAt' | 'isPublished' | 'structureNode'> {
  checked: boolean;
  disabled: boolean;
  handleChange: (checked: boolean, id: string) => void;
}

export const PagesBulkPublishPagesList: FC<Props> = ({
  value = [],
  currentPage,
  pagesListItems,
  pagesListTotalItems,
  pagesListItemsPerPage,
  isLoading,
  disabled,
  onChange,
  handleChangePage,
}) => {
  const handleChange = (checked: boolean, id: string) => {
    onChange?.(checked ? [...value, id] : value.filter((el) => el !== id));
  };

  return (
    <List
      grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
      loading={isLoading}
      pagination={{
        current: currentPage,
        total: pagesListTotalItems,
        pageSize: pagesListItemsPerPage,
        showSizeChanger: false,
        showQuickJumper: true,
        disabled: isLoading,
        onChange: handleChangePage,
        showTotal: (total, range) => `${range[0]}-${range[1]} з ${total} сторінок`,
      }}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Немає сторінок для відображення" /> }}
      dataSource={pagesListItems}
      renderItem={({ id, title, publishedAt, isPublished, structureNode }): ReactElement => (
        <ListItem
          key={id}
          id={id}
          title={title}
          publishedAt={publishedAt}
          isPublished={isPublished}
          structureNode={structureNode}
          checked={value.includes(id)}
          disabled={disabled}
          handleChange={handleChange}
        />
      )}
    />
  );
};

const ListItem: FC<ListItemProps> = ({ id, title, publishedAt, isPublished, structureNode, checked, disabled, handleChange }) => {
  const { token } = theme.useToken();

  const badge: Pick<RibbonProps, 'color' | 'text'> = !publishedAt
    ? { color: token.colorText, text: <Calendar4Week /> }
    : isPublished
      ? { color: 'green', text: <Calendar2Check /> }
      : { color: 'gold', text: <Alarm /> };

  return (
    <List.Item
      colStyle={{ height: '100%' }}
      style={{ height: '100%', marginBlockEnd: 0, paddingBlockEnd: token.margin }}
    >
      <Badge.Ribbon
        rootClassName="pages-bulk-publish-ribbon-wrapper"
        placement="end"
        color={badge.color}
        text={<span>{badge.text}</span>}
      >
        <CheckCard
          size="small"
          style={{ width: '100%', height: '100%', margin: 0 }}
          bodyStyle={{ paddingBlockStart: token.paddingXL }}
          checked={checked}
          disabled={disabled}
          title={
            <Typography.Text ellipsis={{ tooltip: title }}>{title}</Typography.Text>
          }
          description={
            <AwesomeNodePath path={structureNode.path} ellipsis={{ tooltip: structureNode.path }} />
          }
          onChange={(checked) => handleChange(checked, id)}
        />
      </Badge.Ribbon>
    </List.Item>
  );
};